// Thin wrapper around the FastAPI backend.
// Reads the auth token from sessionStorage so calls are authenticated.

import axios from "axios";
import { api } from "../config";

const client = axios.create({
  baseURL: api.API_URL,
  headers: { "Content-Type": "application/json" },
});

client.interceptors.request.use((config) => {
  const raw = sessionStorage.getItem("authUser");
  if (raw) {
    try {
      const parsed = JSON.parse(raw);
      if (parsed && parsed.token) {
        config.headers.Authorization = `Bearer ${parsed.token}`;
      }
    } catch (_) {
      // ignore — bad JSON in session
    }
  }
  return config;
});

client.interceptors.response.use(
  (response) => response.data,
  (error) => {
    const detail = error?.response?.data;
    return Promise.reject(detail || error);
  }
);

export const getLoggedInUserData = () => {
  const raw = sessionStorage.getItem("authUser");
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch (_) {
    return null;
  }
};

const customerIdOf = (override) => {
  if (override !== undefined && override !== null) return override;
  const u = getLoggedInUserData();
  return u?.customer_id ?? 1;
};

const userIdOf = () => getLoggedInUserData()?.user_id;
const isRootAdmin = () => (getLoggedInUserData()?.root_admin ? 1 : 0);

// ---------- Auth ----------
export const apiLogout = (payload = {}) => client.post("/v1/auth/logout", payload);
export const apiResetPassword = (payload) =>
  client.post("/v1/auth/reset-password", payload);
export const apiDashboardWidgets = () => client.get("/v1/auth/home/widgetData");
export const apiDashboardCases = (params = { limit: 10, offset: 0 }) =>
  client.get("/v1/auth/home/caseData", { params });

// ---------- User ----------
export const apiListUsers = ({ customer_id, limit = 10, offset = 0 } = {}) =>
  client.get("/v1/user/list_user", {
    params: { customer_id: customerIdOf(customer_id), limit, offset },
  });

export const apiGetUser = (userId) => client.get(`/v1/user/get_user/${userId}`);

export const apiCreateUser = (payload) => client.post("/v1/user/create_user", payload);

export const apiUpdateUser = (payload) => client.put("/v1/user/update_user_data", payload);

export const apiGetLoginUserData = (customer_id) =>
  client.get(`/v1/user/get_login_user_data/${customerIdOf(customer_id)}`);

export const apiAllUserGroups = () => client.get("/v1/user/all_user_group");

// ---------- Case ----------
export const apiListCases = ({ customer_id, limit = 10, offset = 0 } = {}) =>
  client.get("/v1/case/list_case", {
    params: { customer_id: customerIdOf(customer_id), limit, offset },
  });

export const apiCreateCase = (payload) => client.post("/v1/case/create_case", payload);

export const apiCaseStatusList = () => client.get("/v1/case/case_status_list");

export const apiCaseDetails = (customer_id, case_id) =>
  client.get(`/v1/case/case_details/${customerIdOf(customer_id)}/${case_id}`);

export const apiHomeCaseList = ({ customer_id } = {}) =>
  client.get(
    `/v1/case/home/case_list/${userIdOf()}/${isRootAdmin()}/${customerIdOf(customer_id)}`
  );

// ---------- Role ----------
export const apiListRoles = () => client.get("/v1/role/list_role");
export const apiCreateRole = (payload) => client.post("/v1/role/role_create", payload);

// ---------- Group ----------
export const apiListGroups = (limit = 10) => client.get(`/v1/group/list_group/${limit}`);
export const apiCreateGroup = (payload) => client.post("/v1/group/create_group", payload);

// ---------- Customer ----------
export const apiFetchCustomer = (customer_id) =>
  client.get(`/v1/customer/fetch_customer/${customerIdOf(customer_id)}`);

export default client;
